/**
 * Clubs / forums catalogue. Slug used at /clubs/:slug.
 */

export interface ClubMember {
  role: string;
  name: string;
}

export interface Club {
  slug: string;
  cat: string;
  name: string;
  since: string;
  /** ~30-word teaser shown on the list page. */
  body: string;
  lead: string;
  members: string;
  /** Hex / theme accent for the detail page header band. */
  accent: string;
  /** Long-form mission paragraph(s). */
  mission: string[];
  /** What the club actually does, week-to-week. */
  activities: string[];
  /** Recent / upcoming highlights. */
  highlights?: string[];
  leadership: ClubMember[];
  /** Slugs of events owned by this club, if any. */
  eventSlugs?: string[];
  joinHow: string;
  meetingCadence: string;
  contact: { email: string; phone?: string };
}

export const CLUBS: Club[] = [
  {
    slug: "ieee-student-branch",
    cat: "PROFESSIONAL · IEEE",
    name: "IEEE Student Branch",
    since: "Active since 2003",
    body: "240+ student members across CSE, ECE and ISE. Hosts annual TechXcite, paper-presentation contests, and the IEEE Day celebrations.",
    lead: "Faculty-04",
    members: "240 members",
    accent: "#0a4ea3",
    mission: [
      "The BIET IEEE Student Branch is a chapter of the world's largest professional society of technologists, with student members across the CSE, ECE and ISE departments. We exist to give students a place to write technical papers, present them, hear them critiqued, and network with practising engineers — well before their first job interview.",
      "The branch operates as a teaching organisation as much as a club: every flagship event is run by a student committee, with faculty advisors stepping in only when a vendor cheque or a venue booking demands a signature.",
    ],
    activities: [
      "Weekly paper reading group — Wednesday evenings",
      "Monthly hands-on workshop (rotating: VLSI, networks, signal processing)",
      "Quarterly industry talk — externally hosted speaker, recorded",
      "Annual TechXcite festival — three days, 22 events",
      "IEEE Day celebrations — first Tuesday of October",
    ],
    highlights: [
      "Three first-author papers at IEEE conferences in 2025",
      "Best Student Branch Karnataka Section runner-up · 2024",
      "WIE Adoption scholarship recipients × 2 in 2025-26",
    ],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-04" },
      { role: "Student Branch Chair", name: "Student-Lead-01" },
      { role: "Branch Counsellor", name: "Faculty-12" },
      { role: "Vice Chair (Tech)", name: "Student-Lead-02" },
      { role: "Secretary", name: "Student-Lead-03" },
      { role: "Treasurer", name: "Student-Lead-04" },
    ],
    joinHow:
      "Membership is open year-round to any registered BIET student, but most members join during the orientation week sign-up fair. The branch operates a tiered-mentorship model — every first-year is paired with a senior member for the first semester.",
    meetingCadence: "Every Wednesday · 5:30 PM · CSE Seminar Hall",
    contact: { email: "ieee@bietdvg.edu", phone: "+91 8192 233 850" },
  },
  {
    slug: "ieee-wie",
    cat: "PROFESSIONAL · IEEE",
    name: "IEEE WIE Affinity Group",
    since: "Active since 2018",
    body: "Women in Engineering chapter promoting STEM careers, mentorship circles, and the WIE adoption scholarship program.",
    lead: "Faculty-12",
    members: "118 members",
    accent: "#a32f6c",
    mission: [
      "WIE — Women in Engineering — is the IEEE affinity group dedicated to inspiring, engaging, encouraging and empowering women technologists. The BIET chapter, founded in 2018, focuses on three things: mentorship, scholarship, and visible role models.",
    ],
    activities: [
      "Big-sister mentorship pairing for every first-year woman in the technical branches",
      "Annual WIE Adoption Scholarship — funded externally, awarded internally",
      "Workshop series: 'I built this' — every speaker is a woman engineer working in industry",
      "IEEE WIE international conference travel grants",
    ],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-12" },
      { role: "Chair", name: "Student-Lead-05" },
      { role: "Mentorship Lead", name: "Student-Lead-06" },
    ],
    joinHow: "Open to all women undergraduates and postgraduates. Sign-up at orientation or any time via the chapter email.",
    meetingCadence: "First Friday · 5 PM · ECE Conference Room",
    contact: { email: "wie@bietdvg.edu" },
  },
  {
    slug: "edc",
    cat: "ENTREPRENEURSHIP",
    name: "EDC — Entrepreneurship Cell",
    since: "Active since 2011",
    body: "Runs the Bapuji Innovation Hub, conducts E-summit, ideation sprints, and mentorship sessions with founders.",
    lead: "Faculty-15",
    members: "180 members",
    accent: "#c66a09",
    mission: [
      "The Entrepreneurship Cell at BIET is a place for students who want to build something — not just companies, but products, projects and the discipline of shipping. The cell operates the Bapuji Innovation Hub: a permanent two-room workspace inside the central library with whiteboards, soldering stations, and a small fund for prototyping consumables.",
    ],
    activities: [
      "Weekly idea-stand-up — anyone with a project pitches in 90 seconds",
      "Monthly founder visit — Bengaluru / Mysuru-based founders walk students through a hard problem they faced",
      "Annual E-Summit — 2 days, pitch competition, founder panels",
      "Summer prototyping grants — small grants, recipients commit to a public demo at the end of summer",
    ],
    highlights: [
      "Three current Y-Combinator alumni cite EDC in their backstory",
      "Innovation Hub has hosted 40+ student projects since opening",
    ],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-15" },
      { role: "President", name: "Student-Lead-07" },
      { role: "Mentor Coordinator", name: "Student-Lead-08" },
    ],
    joinHow:
      "Three signed students with a half-page proposal can request access to the Innovation Hub. Cell membership is open to anyone who attends three consecutive idea stand-ups.",
    meetingCadence: "Tuesday + Thursday · 4 PM · Innovation Hub",
    contact: { email: "edc@bietdvg.edu", phone: "+91 8192 233 851" },
  },
  {
    slug: "datin",
    cat: "DATA · TECHNICAL",
    name: "DATIN — Data Analytics Forum",
    since: "Active since 2019",
    body: "Student-run forum for ML/AI/data engineering enthusiasts. Weekly workshops, Kaggle nights, and a curated reading group.",
    lead: "Faculty-22",
    members: "165 members",
    accent: "#3b8a5a",
    mission: [
      "DATIN is the data-and-AI affinity forum for BIET's CS-DS-ISE-AIML community. We do three things: run hands-on workshops on real tools (PyTorch, dbt, Airflow), host Kaggle and competitive-programming nights, and maintain a curated reading list of ML papers that members read together.",
      "DATIN hosted the inaugural Code Combat 1.0 contest in December 2025 — the chapter's first inter-collegiate programming competition.",
    ],
    activities: [
      "Tuesday workshop · rotating tooling track",
      "Friday Kaggle night · live notebook battles",
      "Saturday paper reading · 1 ML paper, 1 hour, no slides",
      "Hosting Code Combat (annual)",
    ],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-22" },
      { role: "President", name: "Student-Lead-09" },
      { role: "Workshop Lead", name: "Student-Lead-10" },
    ],
    eventSlugs: ["code-combat-1"],
    joinHow: "Open to anyone willing to commit to one workshop a month. Sign-up form is permanently open via the DATIN website.",
    meetingCadence: "Tuesday + Friday · 5 PM · DATIN Lab (CSE Block)",
    contact: { email: "datin@bietdvg.edu" },
  },
  {
    slug: "robotics",
    cat: "ROBOTICS",
    name: "Robotics Club",
    since: "Active since 2008",
    body: "Builds for Robocon and SAE BAJA. Active in line-follower, drone, and mecanum-platform builds. Lab open 24×7 to members.",
    lead: "Faculty-09",
    members: "92 members",
    accent: "#3a3a3a",
    mission: [
      "The BIET Robotics Club is a build-first community: every member commits to at least one robot per academic year. The club's workshop is a 24×7 facility with welding, CNC milling, soldering, and a 3D-printer farm. Faculty advice is available; build decisions are not made by committee.",
    ],
    activities: [
      "Weekly open-build session · members work on personal or team projects",
      "Annual Robocon prep — Mar–Jun build cycle",
      "SAE BAJA cross-listing with the BAJA team",
      "Workshops on micro-controller programming, sensor integration, and CAD",
    ],
    highlights: [
      "Robocon India national finalists × 4 years",
      "Mecanum drive platform open-sourced (CC-BY) in 2024",
    ],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-09" },
      { role: "Captain", name: "Student-Lead-11" },
      { role: "Workshop Manager", name: "Student-Lead-12" },
    ],
    joinHow: "Membership requires demonstrating one finished robot — even a small one. The club hosts an entry workshop every August for prospective members.",
    meetingCadence: "Open-build Monday-Thursday · 5–9 PM · Robotics Workshop",
    contact: { email: "robotics@bietdvg.edu" },
  },
  {
    slug: "baja-formula",
    cat: "AUTOMOTIVE",
    name: "BAJA SAE & Formula Bharat Team",
    since: "Active since 2010",
    body: "Cross-discipline student team designing and racing single-seater off-road and formula vehicles. National-level finalists.",
    lead: "Faculty-18",
    members: "55 members",
    accent: "#9b1d28",
    mission: [
      "Team Aurora — BIET's BAJA SAE / Formula Bharat team — designs, builds and races single-seater off-road and formula vehicles in national-level student competitions. Membership is open across departments but the team's heart is in the Mechanical Engineering workshop.",
    ],
    activities: [
      "Sep-Mar build season for BAJA",
      "Apr-Jul build season for Formula Bharat",
      "Sub-team formats — design, drivetrain, suspension, electronics, chassis, finance",
      "Annual cost report and presentation prep",
    ],
    highlights: [
      "SAE BAJA 2026 South Zone — 2nd place",
      "Formula Bharat 2025 — top-15 nationally in cost report",
    ],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-18" },
      { role: "Team Captain", name: "Student-Lead-13" },
    ],
    joinHow: "Recruitment happens once a year, in August, with a 4-day technical interview. Cross-department members are explicitly welcomed.",
    meetingCadence: "Daily during build season · Mech Workshop",
    contact: { email: "aurora@bietdvg.edu" },
  },
  {
    slug: "coding-club",
    cat: "CODING",
    name: "Coding Club",
    since: "Active since 2014",
    body: "Competitive programming, open-source contribution drives, and the annual Hackoctober hackathon.",
    lead: "Faculty-26",
    members: "210 members",
    accent: "#1f5e54",
    mission: [
      "The Coding Club at BIET focuses on the muscle-memory side of software: writing code that runs, breaks, debugs, refactors, ships. We don't compete on titles — we compete on commits.",
    ],
    activities: [
      "Tuesday CP problem set · solutions discussed Friday",
      "Open-source contribution drive every October (Hacktoberfest)",
      "Annual Hackoctober hackathon",
      "Code reviews on student capstone projects",
    ],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-26" },
      { role: "President", name: "Student-Lead-14" },
    ],
    eventSlugs: ["web-dev-sprint"],
    joinHow: "Open membership. Members commit to one solved problem a week or one PR a month — the only red line.",
    meetingCadence: "Tuesday + Friday · 5 PM · CSE Lab 3",
    contact: { email: "codingclub@bietdvg.edu" },
  },
  {
    slug: "quill",
    cat: "LITERARY",
    name: "Quill — Literary Society",
    since: "Active since 1998",
    body: "Publishes the campus magazine Pravaha. Hosts open-mic, debate, and slam-poetry evenings each fortnight.",
    lead: "Faculty-31",
    members: "84 members",
    accent: "#5b3a8a",
    mission: [
      "Quill is the literary heartbeat of the BIET campus. We read together, write together, argue together, and stage open-mics under the trees behind the central library every other Saturday. We are most proud of Pravaha — the campus magazine that has been continuously published since 1999.",
    ],
    activities: [
      "Fortnightly open-mic · poetry, fiction, spoken word",
      "Monthly book club · book chosen by majority vote",
      "Quarterly debate league · against neighbouring colleges",
      "Annual Pravaha magazine — 80 pages, hand-stitched",
    ],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-31" },
      { role: "Editor, Pravaha", name: "Student-Lead-15" },
    ],
    joinHow: "Show up to one open-mic. Speak. You're in.",
    meetingCadence: "Alternate Saturdays · 6 PM · Library quad",
    contact: { email: "quill@bietdvg.edu" },
  },
  {
    slug: "sangam",
    cat: "CULTURAL",
    name: "Sangam — Cultural Forum",
    since: "Active since 1990",
    body: "Music, dance, theatre. Stages the annual two-day Spandana cultural festival drawing 12,000+ attendees.",
    lead: "Faculty-08",
    members: "320 members",
    accent: "#c89c00",
    mission: [
      "Sangam is the cultural forum that runs music, dance, and theatre across the BIET calendar. Spandana — Sangam's annual two-day festival — is the largest student-run cultural event in central Karnataka, with over 12,000 attendees in 2025.",
    ],
    activities: [
      "Five sub-clubs: Symphony (music), Spandana (dance), Drama, Cine, Lit-fest",
      "Weekly rehearsals during festival prep",
      "Annual Spandana festival — January",
      "Anubhava cultural night at year end",
    ],
    eventSlugs: ["anubhava-2026"],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-08" },
      { role: "President", name: "Student-Lead-16" },
    ],
    joinHow: "Auditions for performance roles every August. Production / coordination roles open year-round.",
    meetingCadence: "Daily during festival prep · Open Air Theatre",
    contact: { email: "sangam@bietdvg.edu" },
  },
  {
    slug: "nss",
    cat: "CIVIC",
    name: "NSS Unit",
    since: "National Service Scheme",
    body: "Two units, 200 volunteers. Runs the rural outreach program in Honnali taluk; blood donation, swachh-bharat camps.",
    lead: "Faculty-14",
    members: "200 volunteers",
    accent: "#2a6f2a",
    mission: [
      "BIET's National Service Scheme units run rural-outreach work, public-health camps, and the institute's blood-donation drive. Every NSS volunteer commits to 120 hours of service across the academic year.",
    ],
    activities: [
      "Weekly rural-outreach trip — Honnali taluk",
      "Blood-donation camps — 2 per semester",
      "Swachh-bharat campus and town drives",
      "Annual seven-day camp — special-camping module",
    ],
    leadership: [
      { role: "NSS Programme Officer", name: "Faculty-14" },
      { role: "Unit-1 Lead", name: "Student-Lead-17" },
      { role: "Unit-2 Lead", name: "Student-Lead-18" },
    ],
    joinHow: "Sign up via the NSS office. First-year students are encouraged to register; NSS hours appear on the official transcript.",
    meetingCadence: "Saturday morning briefings · NSS Office",
    contact: { email: "nss@bietdvg.edu" },
  },
  {
    slug: "ncc",
    cat: "DEFENCE / NCC",
    name: "NCC Air Wing",
    since: "Affiliated 2 KAR Air Sqn",
    body: "Cadets earn A, B and C certificates. Annual training camps, RDC selections, and adventure activities.",
    lead: "Lt. Faculty-19",
    members: "76 cadets",
    accent: "#1c4a85",
    mission: [
      "BIET's NCC Air Wing — affiliated with 2 Karnataka Air Squadron — trains cadets through the A, B, and C certificate programmes, providing a foundation in service discipline, drill, leadership and aviation theory.",
    ],
    activities: [
      "Weekly drill · parade ground",
      "Annual training camps",
      "Republic Day Camp (RDC) selection rounds",
      "Adventure activities — trekking, gliding orientation",
    ],
    leadership: [
      { role: "Caretaker Officer", name: "Lt. Faculty-19" },
      { role: "Senior Under-Officer", name: "Cadet-Lead-01" },
    ],
    joinHow: "First-year enrolment in August. Selection involves a fitness test and an interview.",
    meetingCadence: "Wednesday + Saturday · 6 AM · Parade Ground",
    contact: { email: "ncc@bietdvg.edu" },
  },
  {
    slug: "green-campus",
    cat: "ENVIRONMENT",
    name: "Green Campus Forum",
    since: "Active since 2017",
    body: "Drives the campus's solar-roof, water-harvesting and waste-segregation programs. Tree census every monsoon.",
    lead: "Faculty-30",
    members: "60 members",
    accent: "#3a6e2a",
    mission: [
      "The Green Campus Forum exists to make the BIET campus measurably more sustainable, year over year. We do not write reports we cannot back with data; we keep an active dashboard of solar generation, water harvested, and waste segregated, all maintained by students.",
    ],
    activities: [
      "Quarterly tree census across the campus",
      "Solar-roof generation dashboard maintenance",
      "Waste-segregation audit — every fortnight",
      "Rainwater-harvesting inspection during the monsoon",
    ],
    leadership: [
      { role: "Faculty Advisor", name: "Faculty-30" },
      { role: "Lead", name: "Student-Lead-19" },
    ],
    joinHow: "Open membership. New members start by joining one tree census or one waste audit.",
    meetingCadence: "Monthly · Sustainability Cell",
    contact: { email: "green@bietdvg.edu" },
  },
];

export function getClub(slug: string): Club | undefined {
  return CLUBS.find((c) => c.slug === slug);
}
